import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useVirtualizer } from '@tanstack/react-virtual'
import { format, isThisYear, isToday, isYesterday } from 'date-fns'
import { ChevronLeft, ExternalLink, Loader2, MoreVertical, Users } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { formatTimeShort } from '@/shared/lib/DateUtil'
import { useDebounce } from '@/shared/hooks/useDebounce'
import {
  CHAT_COPY,
  chatErrorMessage,
  chatKeys,
  chatStatus,
  cursorsFor,
  deliveryStateForMessage,
  flattenMessages,
  isChatAccessRevoked,
  otherSideMembers,
  useChatAccountId,
  useChatCapabilities,
  useChatOutboxStore,
  useChatUiStore,
  useChatUserId,
  useConversation,
  useConversations,
  useDeleteMessage,
  useMarkRead,
  useMessages,
  useQuotedMessages,
  useRetrySend,
  useSetMuted,
  type ChatConversation,
  type ChatMessage,
  type OutboxMessage,
} from '@/entities/chat'
import { conversationKind, groupByDay, isInternalKind, isSystemMessage, lastReadableMessageId } from './chatHelpers'
import { CallsignRail } from './CallsignRail'
import { MessageBubble } from './MessageBubble'
import { SystemMessageRow } from './SystemMessageRow'
import { MessageInfo } from './MessageInfo'
import { DeleteMessageDialog } from './DeleteMessageDialog'
import { ChatAccessEnded } from './ChatAccessEnded'
import { ChatComposer } from './ChatComposer'
import { DaySeparator } from './DaySeparator'
import { UnreadDivider } from './UnreadDivider'
import { GroupMembersDialog } from './GroupMembersDialog'
import { PersonAvatar } from './PersonAvatar'
import { GroupAvatar } from './GroupAvatar'

const EMPTY_OUTBOX: OutboxMessage[] = []
const NEAR_BOTTOM_PX = 160
const LOAD_OLDER_THRESHOLD_PX = 64

type ThreadRow =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'unread'; key: string }
  | { kind: 'message'; key: string; message: ChatMessage }
  | { kind: 'outbox'; key: string; item: OutboxMessage }

function isNearBottom(element: HTMLDivElement | null): boolean {
  return !element || element.scrollHeight - element.scrollTop - element.clientHeight < NEAR_BOTTOM_PX
}

function dayLabel(day: string): string {
  const date = new Date(day)
  if (isToday(date)) return 'Today'
  if (isYesterday(date)) return 'Yesterday'
  return isThisYear(date) ? format(date, 'MMMM d') : format(date, 'MMMM d, yyyy')
}

function outboxToMessage(item: OutboxMessage, myUserId: number | null): ChatMessage {
  return {
    id: -1,
    conversationId: item.conversationId,
    senderUserId: myUserId ?? 0,
    senderRole: 'Staff',
    senderName: '',
    clientRef: item.clientRef,
    sentAt: item.sentAt,
    isDeleted: false,
    content: {
      body: item.body,
      attachments: item.attachments,
      mentions: item.mentions,
      replyToMessageId: item.replyToMessageId,
    },
    kind: 'User',
  }
}

function buildRows(messages: ChatMessage[], outboxItems: OutboxMessage[], unreadThresholdId: number | null): ThreadRow[] {
  const rows: ThreadRow[] = []
  const dayGroups = groupByDay(messages)
  let unreadInserted = false

  for (const group of dayGroups) {
    rows.push({ kind: 'day', key: `day-${group.day}`, label: dayLabel(group.day) })
    for (const message of group.messages) {
      if (!unreadInserted && unreadThresholdId != null && message.id > unreadThresholdId) {
        rows.push({ kind: 'unread', key: 'unread-divider' })
        unreadInserted = true
      }
      rows.push({ kind: 'message', key: `message-${message.id}`, message })
    }
  }

  if (outboxItems.length > 0) {
    const outboxDay = new Date(outboxItems[0].sentAt).toDateString()
    const lastGroup = dayGroups[dayGroups.length - 1]
    if (!lastGroup || lastGroup.day !== outboxDay) {
      rows.push({ kind: 'day', key: `day-${outboxDay}`, label: dayLabel(outboxDay) })
    }
    for (const item of outboxItems) {
      rows.push({ kind: 'outbox', key: `outbox-${item.clientRef}`, item })
    }
  }

  return rows
}

function collectMissingReplyIds(messages: ChatMessage[], outboxItems: OutboxMessage[]): number[] {
  const loaded = new Set(messages.map((message) => message.id))
  const missing = new Set<number>()
  for (const message of messages) {
    const replyToMessageId = message.content?.replyToMessageId
    if (replyToMessageId != null && !loaded.has(replyToMessageId)) missing.add(replyToMessageId)
  }
  for (const item of outboxItems) {
    if (item.replyToMessageId != null && !loaded.has(item.replyToMessageId)) missing.add(item.replyToMessageId)
  }
  return [...missing]
}

function buildQuotedIndex(messages: ChatMessage[], quoted: ChatMessage[] | undefined): Map<number, ChatMessage> {
  const index = new Map<number, ChatMessage>()
  for (const message of messages) index.set(message.id, message)
  for (const message of quoted ?? []) index.set(message.id, message)
  return index
}

function quotedTextFor(index: Map<number, ChatMessage>, replyToMessageId: number | null | undefined): string | null {
  if (replyToMessageId == null) return null
  const target = index.get(replyToMessageId)
  if (!target) return null
  if (target.isDeleted || !target.content) return 'Message deleted'
  if (target.content.body) return target.content.body
  return target.content.attachments.length > 0 ? 'Attachment' : ''
}

export function ChatThread({
  conversationId,
  variant,
  onBack,
  onSendAndAdvance,
}: {
  conversationId: number
  variant: 'dock' | 'page'
  onBack?: () => void
  onSendAndAdvance?: () => void
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const accountId = useChatAccountId()
  const myUserId = useChatUserId()
  const { canChat } = useChatCapabilities()

  const detailQuery = useConversation(conversationId)
  const listSummary = useConversations().data?.find((conversation) => conversation.id === conversationId)
  const messagesQuery = useMessages(conversationId)

  const { mutate: markConversationRead } = useMarkRead()
  const { mutate: deleteMessage, isPending: isDeletingMessage } = useDeleteMessage()
  const { mutate: setMuted } = useSetMuted()
  const retrySend = useRetrySend()

  const setVisibleConversation = useChatUiStore((state) => state.setVisibleConversation)
  const setSelectedConversation = useChatUiStore((state) => state.setSelectedConversation)
  const setReplyTo = useChatOutboxStore((state) => state.setReplyTo)
  const discardOutboxMessage = useChatOutboxStore((state) => state.discard)
  const draftReplyToId = useChatOutboxStore((state) => state.drafts[conversationId]?.replyToMessageId ?? null)
  const outboxItems = useChatOutboxStore((state) => state.outbox[conversationId] ?? EMPTY_OUTBOX)

  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)
  const [infoTargetId, setInfoTargetId] = useState<number | null>(null)
  const [membersOpen, setMembersOpen] = useState(false)

  const parentRef = useRef<HTMLDivElement>(null)
  const lastAnchorKeyRef = useRef<string | null>(null)
  const olderLoadRef = useRef<{ height: number } | null>(null)
  const unreadThresholdRef = useRef<{ conversationId: number; id: number | null } | null>(null)
  const openMarkedConversationRef = useRef<number | null>(null)
  const lastMarkedReadRef = useRef<{ conversationId: number; messageId: number } | null>(null)

  const messages = flattenMessages(messagesQuery.data?.pages)
  const missingReplyIds = collectMissingReplyIds(messages, outboxItems)
  const quotedQuery = useQuotedMessages(conversationId, missingReplyIds)
  const quotedIndex = buildQuotedIndex(messages, quotedQuery.data)

  if (
    detailQuery.data &&
    (unreadThresholdRef.current === null || unreadThresholdRef.current.conversationId !== conversationId)
  ) {
    unreadThresholdRef.current = { conversationId, id: detailQuery.data.summary.myLastReadMessageId }
  }

  const unreadThresholdId =
    unreadThresholdRef.current?.conversationId === conversationId ? unreadThresholdRef.current.id : null
  const rows = buildRows(messages, outboxItems, unreadThresholdId)

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 72,
    getItemKey: (index) => rows[index].key,
    overscan: 8,
  })

  useEffect(() => {
    setVisibleConversation(conversationId)
    return () => setVisibleConversation(null)
  }, [conversationId, setVisibleConversation])

  const newestReadableMessageId = lastReadableMessageId(messages)

  useEffect(() => {
    if (newestReadableMessageId == null) return
    if (openMarkedConversationRef.current === conversationId) return
    openMarkedConversationRef.current = conversationId
    lastMarkedReadRef.current = { conversationId, messageId: newestReadableMessageId }
    markConversationRead({ conversationId, lastReadMessageId: newestReadableMessageId })
  }, [conversationId, newestReadableMessageId, markConversationRead])

  const readCursorTarget = useMemo(
    () => (newestReadableMessageId == null ? null : { conversationId, messageId: newestReadableMessageId }),
    [conversationId, newestReadableMessageId],
  )
  const debouncedReadCursorTarget = useDebounce(readCursorTarget, 1500)

  const advanceReadCursor = useCallback(
    (messageId: number) => {
      if (document.visibilityState !== 'visible') return
      if (!isNearBottom(parentRef.current)) return
      const alreadyMarked = lastMarkedReadRef.current
      if (alreadyMarked && alreadyMarked.conversationId === conversationId && alreadyMarked.messageId >= messageId) {
        return
      }
      lastMarkedReadRef.current = { conversationId, messageId }
      markConversationRead({ conversationId, lastReadMessageId: messageId })
    },
    [conversationId, markConversationRead],
  )

  useEffect(() => {
    if (debouncedReadCursorTarget == null) return
    if (debouncedReadCursorTarget.conversationId !== conversationId) return
    advanceReadCursor(debouncedReadCursorTarget.messageId)
  }, [conversationId, debouncedReadCursorTarget, advanceReadCursor])

  const [visibleSignal, setVisibleSignal] = useState(0)
  const debouncedVisibleSignal = useDebounce(visibleSignal, 1500)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') setVisibleSignal((value) => value + 1)
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  useEffect(() => {
    if (debouncedVisibleSignal === 0) return
    if (newestReadableMessageId == null) return
    advanceReadCursor(newestReadableMessageId)
  }, [debouncedVisibleSignal, newestReadableMessageId, advanceReadCursor])

  useEffect(() => {
    if (rows.length === 0) return
    const key = rows[rows.length - 1].key
    if (lastAnchorKeyRef.current === key) return
    const isFirstAnchor = lastAnchorKeyRef.current == null
    lastAnchorKeyRef.current = key
    if (isFirstAnchor || isNearBottom(parentRef.current)) {
      virtualizer.scrollToIndex(rows.length - 1, { align: 'end' })
    }
  }, [rows, virtualizer])

  useEffect(() => {
    if (messagesQuery.isFetchingNextPage) return
    const pending = olderLoadRef.current
    if (!pending) return
    const element = parentRef.current
    if (element) {
      const delta = virtualizer.getTotalSize() - pending.height
      element.scrollTop += delta
    }
    olderLoadRef.current = null
  }, [messagesQuery.isFetchingNextPage, virtualizer])

  const revokedSource = detailQuery.isError ? detailQuery.error : messagesQuery.isError ? messagesQuery.error : null
  const isRevoked = revokedSource != null && (isChatAccessRevoked(revokedSource) || chatStatus(revokedSource) === 404)

  useEffect(() => {
    if (!isRevoked || accountId == null) return
    queryClient.setQueryData<ChatConversation[]>(chatKeys.conversations(accountId), (existing) =>
      existing?.filter((row) => row.id !== conversationId),
    )
    queryClient.removeQueries({ queryKey: chatKeys.conversation(accountId, conversationId) })
    queryClient.removeQueries({ queryKey: chatKeys.messages(accountId, conversationId) })
    setSelectedConversation(null)
  }, [isRevoked, accountId, conversationId, queryClient, setSelectedConversation])

  const handleScroll = () => {
    const element = parentRef.current
    if (!element) return
    if (element.scrollTop > LOAD_OLDER_THRESHOLD_PX) return
    if (!messagesQuery.hasNextPage || messagesQuery.isFetchingNextPage) return
    if (olderLoadRef.current) return
    olderLoadRef.current = { height: virtualizer.getTotalSize() }
    void messagesQuery.fetchNextPage()
  }

  const handleConfirmDelete = () => {
    if (deleteTargetId == null) return
    setDeleteError(null)
    deleteMessage(
      { conversationId, messageId: deleteTargetId },
      {
        onSuccess: () => setDeleteTargetId(null),
        onError: (deleteMessageError) => setDeleteError(chatErrorMessage(deleteMessageError, CHAT_COPY.deleteFailed)),
      },
    )
  }

  const handleBack = () => {
    if (onBack) onBack()
    else navigate({ to: '/' })
  }

  if (detailQuery.isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-ink-4" />
      </div>
    )
  }

  if (isRevoked) {
    if (isChatAccessRevoked(revokedSource)) return <ChatAccessEnded />
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
        <p className="text-[13px] text-ink-2">{CHAT_COPY.notAvailable}</p>
        <Button variant="outline" size="sm" onClick={handleBack}>
          {CHAT_COPY.notAvailableAction}
        </Button>
      </div>
    )
  }

  if (detailQuery.isError || !detailQuery.data) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
        <p className="text-[13px] font-medium text-ink">{CHAT_COPY.threadFailed}</p>
        <Button variant="outline" size="sm" onClick={() => detailQuery.refetch()}>
          {CHAT_COPY.retry}
        </Button>
      </div>
    )
  }

  const detail = detailQuery.data
  const memberAvatars = new Map(
    detail.members.map((member) => [
      member.userId,
      { photoUrl: member.photoUrl ?? null, photoFileId: member.photoFileId ?? null },
    ]),
  )
  const kind = conversationKind(detail.summary.type)
  const internal = isInternalKind(kind)
  const others = otherSideMembers(detail.members, 'Staff', myUserId, internal)
  const cursors = cursorsFor(others)
  const clientId = detail.summary.clientId
  const replyPreviewText = quotedTextFor(quotedIndex, draftReplyToId)
  const replyPreview = replyPreviewText ? `Replying to: ${replyPreviewText.slice(0, 60)}` : null

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        {variant === 'dock' && (
          <button
            type="button"
            onClick={handleBack}
            aria-label="Back"
            className="shrink-0 rounded-md p-1 text-ink-3 hover:bg-hover hover:text-ink"
          >
            <ChevronLeft size={16} />
          </button>
        )}
        {detail.summary.type === 'Direct' ? (
          <PersonAvatar
            name={detail.summary.title}
            photoUrl={detail.summary.photoUrl}
            photoFileId={detail.summary.photoFileId}
          />
        ) : detail.summary.type === 'Group' ? (
          <GroupAvatar members={listSummary?.memberPreviews ?? []} callsign={detail.summary.callsign} />
        ) : (
          <CallsignRail callsign={detail.summary.callsign} />
        )}
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13.5px] font-semibold text-ink">{detail.summary.title}</p>
          <p className="truncate text-[11.5px] text-ink-3">
            {kind === 'client'
              ? `${detail.clientUserCount} client users · ops desk`
              : kind === 'direct'
                ? CHAT_COPY.directSubtitle
                : CHAT_COPY.groupSubtitle(detail.members.length)}
          </p>
        </div>
        {clientId != null && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate({ to: '/clients/$clientId', params: { clientId: String(clientId) } })}
          >
            <ExternalLink size={13} />
            Open client
          </Button>
        )}
        {kind === 'group' && canChat && (
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={CHAT_COPY.groupMembers}
            onClick={() => setMembersOpen(true)}
          >
            <Users size={15} />
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label="Conversation options">
              <MoreVertical size={15} />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuCheckboxItem
              checked={detail.summary.muted}
              onCheckedChange={(checked) => setMuted({ conversationId, muted: checked })}
            >
              Mute
            </DropdownMenuCheckboxItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {messagesQuery.isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-ink-4" />
        </div>
      ) : messagesQuery.isError ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 px-6 text-center">
          <p className="text-[13px] font-medium text-ink">{CHAT_COPY.threadFailed}</p>
          <Button variant="outline" size="sm" onClick={() => messagesQuery.refetch()}>
            {CHAT_COPY.retry}
          </Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-1 items-center justify-center px-6 text-center text-[13px] text-ink-3">
          {kind === 'client'
            ? CHAT_COPY.threadEmptyStaff
            : kind === 'direct'
              ? CHAT_COPY.threadEmptyDirect(detail.summary.title)
              : CHAT_COPY.threadEmptyGroup}
        </div>
      ) : (
        <div ref={parentRef} onScroll={handleScroll} className="relative flex-1 overflow-y-auto">
          {messagesQuery.isFetchingNextPage && (
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 flex justify-center py-2">
              <span className="flex items-center gap-1.5 rounded-full border border-line bg-surface px-2.5 py-1 text-[11.5px] text-ink-3 shadow-sm">
                <Loader2 className="h-3 w-3 animate-spin" />
                {CHAT_COPY.loadingOlder}
              </span>
            </div>
          )}
          <div style={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <div
                  key={row.key}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  {row.kind === 'day' && <DaySeparator label={row.label} />}
                  {row.kind === 'unread' && <UnreadDivider />}
                  {row.kind === 'message' && isSystemMessage(row.message) && (
                    <SystemMessageRow message={row.message} />
                  )}
                  {row.kind === 'message' && !isSystemMessage(row.message) && (
                    <MessageBubble
                      conversationId={conversationId}
                      message={row.message}
                      mine={row.message.senderUserId === myUserId}
                      senderAvatar={
                        detail.summary.type === 'Group'
                          ? (memberAvatars.get(row.message.senderUserId) ?? { photoUrl: null, photoFileId: null })
                          : null
                      }
                      time={formatTimeShort(row.message.sentAt)}
                      deliveryState={
                        row.message.senderUserId === myUserId
                          ? deliveryStateForMessage(row.message.id, cursors)
                          : null
                      }
                      quotedText={quotedTextFor(quotedIndex, row.message.content?.replyToMessageId)}
                      onReply={() => setReplyTo(conversationId, row.message.id)}
                      onDelete={
                        !internal || row.message.senderUserId === myUserId
                          ? () => {
                              setDeleteTargetId(row.message.id)
                              setDeleteError(null)
                            }
                          : undefined
                      }
                      onShowInfo={() => setInfoTargetId(row.message.id)}
                      showRoleTag={!internal}
                    />
                  )}
                  {row.kind === 'outbox' && (
                    <MessageBubble
                      conversationId={conversationId}
                      message={outboxToMessage(row.item, myUserId)}
                      mine
                      time={formatTimeShort(row.item.sentAt)}
                      deliveryState={row.item.status === 'failed' ? 'failed' : 'pending'}
                      quotedText={quotedTextFor(quotedIndex, row.item.replyToMessageId)}
                      error={row.item.error}
                      onRetry={() => retrySend(row.item)}
                      onDiscard={() => discardOutboxMessage(conversationId, row.item.clientRef)}
                      showRoleTag={!internal}
                    />
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      <ChatComposer
        conversationId={conversationId}
        members={detail.members}
        hintText={
          kind === 'client'
            ? CHAT_COPY.composerHint(detail.clientUserCount, detail.summary.title)
            : kind === 'direct'
              ? CHAT_COPY.composerHintDirect(detail.summary.title)
              : CHAT_COPY.composerHintGroup
        }
        replyPreview={replyPreview}
        canPost={detail.summary.canPost}
        showChips={!internal}
        onSendAndAdvance={onSendAndAdvance}
      />

      <DeleteMessageDialog
        open={deleteTargetId != null}
        onOpenChange={(open) => {
          if (!open) {
            setDeleteTargetId(null)
            setDeleteError(null)
          }
        }}
        onConfirm={handleConfirmDelete}
        isPending={isDeletingMessage}
        error={deleteError}
        internal={internal}
      />

      <MessageInfo
        others={others}
        messageId={infoTargetId ?? 0}
        open={infoTargetId != null}
        onOpenChange={(open) => {
          if (!open) setInfoTargetId(null)
        }}
      />

      {kind === 'group' && (
        <GroupMembersDialog
          conversationId={conversationId}
          open={membersOpen}
          onOpenChange={setMembersOpen}
          onLeft={handleBack}
        />
      )}
    </div>
  )
}
